import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Space,
  Table,
  Typography,
  Switch,
  Select,
} from "antd";

import dayjs from "dayjs";
import React from "react";
import UserStatusTag, { status } from "../shared/UserStatusTag";
import { formatDateTime } from "../../helpers/date";

const { TextArea } = Input;
const { Text } = Typography;
const TOPUP_PACKAGES = [20, 50, 100, 500, 1000, 2000, 3000, 5000];

const renderCampaignTable = (fieldPathPrefix) => (
  <Table
    bordered
    pagination={false}
    rowKey="packageAmount"
    dataSource={TOPUP_PACKAGES.map((amount, index) => ({
      packageAmount: amount,
      rowIndex: index,
    }))}
    columns={[
      {
        title: "Package (RM)",
        dataIndex: "packageAmount",
        render: (_, record) => (
          <>
            <Form.Item
              name={[...fieldPathPrefix, record.rowIndex, "packageAmount"]}
              initialValue={record.packageAmount}
              hidden
            >
              <Input />
            </Form.Item>
            <span>{`RM ${record.packageAmount}`}</span>
          </>
        ),
      },
      {
        title: "Bonus (RM)",
        render: (_, record) => (
          <Form.Item
            name={[...fieldPathPrefix, record.rowIndex, "bonus"]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              min={0}
              precision={2}
              placeholder="Optional bonus amount"
              style={{ width: "100%" }}
            />
          </Form.Item>
        ),
      },
      {
        title: "Limit (Optional)",
        render: (_, record) => (
          <Form.Item
            name={[...fieldPathPrefix, record.rowIndex, "limit"]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              min={1}
              placeholder="No limit"
              style={{ width: "100%" }}
            />
          </Form.Item>
        ),
      },
    ]}
  />
);

const renderEditTable = (fieldPathPrefix) => (
  <Table
    bordered
    pagination={false}
    rowKey="packageAmount"
    dataSource={TOPUP_PACKAGES.map((amount, index) => ({
      packageAmount: amount,
      rowIndex: index,
    }))}
    size="small"
    columns={[
      {
        title: "Package (RM)",
        dataIndex: "packageAmount",
        render: (_, record) => (
          <>
            <Form.Item
              name={[...fieldPathPrefix, record.rowIndex, "packageAmount"]}
              initialValue={record.packageAmount}
              hidden
            >
              <Input />
            </Form.Item>
            <span>RM {record.packageAmount}</span>
          </>
        ),
      },
      {
        title: "Bonus (RM)",
        render: (_, record) => (
          <Form.Item
            name={[...fieldPathPrefix, record.rowIndex, "bonus"]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              min={0}
              precision={2}
              placeholder="Optional bonus amount"
              style={{ width: "100%" }}
            />
          </Form.Item>
        ),
      },
      {
        title: "Limit",
        render: (_, record) => (
          <Form.Item
            name={[...fieldPathPrefix, record.rowIndex, "limit"]}
            style={{ marginBottom: 0 }}
          >
            <InputNumber
              min={1}
              placeholder="No limit"
              style={{ width: "100%" }}
            />
          </Form.Item>
        ),
      },
    ]}
  />
);

const BonusCampaignForm = ({
  form,
  loading,
  mode = "create",
  onSubmit,
  onCancel,
  submitText,
  selectedCampaign = {},
}) => {
  const isEditMode = mode !== "create";

  console.log("selectedCampaign : ", selectedCampaign);

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 16,
        boxShadow: "0 6px 24px rgba(0,0,0,0.06)",
        background: "#fcfcfd",
      }}
    >
      <Form
        form={form}
        name="new-bonus-campaign-form"
        layout="vertical"
        autoComplete="off"
        initialValues={{ category: "all" }}
        onFinish={onSubmit}
      >
        <div style={{ marginBottom: 20 }}>
          <Typography.Title level={4} style={{ marginBottom: 4 }}>
            {isEditMode ? "Edit Bonus Campaign" : "Create New Bonus Campaign"}
          </Typography.Title>
          <Text type="secondary">
            Campaigns are created <strong>inactive</strong> by default. Activate
            them from the list after creation.
          </Text>
        </div>

        {/* Basic Info */}
        <Card
          size="small"
          bordered={false}
          style={{
            marginBottom: 20,
            background: "#fff",
            borderRadius: 12,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <Form.Item
                label="Target"
                name="category"
                rules={[{ required: true }]}
              >
                <Radio.Group
                  disabled={isEditMode}
                  options={[
                    { label: "All Users", value: "all" },
                    { label: "By User Status", value: "userStatus" },
                  ]}
                  optionType="button"
                  buttonStyle="solid"
                  onChange={() => {
                    const category = form.getFieldValue("category");
                    form.resetFields();
                    form.setFieldsValue({ category });
                  }}
                />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Campaign Name"
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Please enter campaign name",
                  },
                  { max: 50, message: "Max 50 characters" },
                ]}
              >
                <Input placeholder="e.g. Raya Bonus Campaign" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Campaign Period (cannot be changed)"
                name="campaignPeriod"
                rules={[
                  {
                    required: selectedCampaign ? false : true,
                    message: "Please select campaign period",
                  },
                ]}
              >
                {selectedCampaign.startDate && selectedCampaign.endDate ? (
                  <div>
                    <Text>
                      {selectedCampaign.startDate
                        ? formatDateTime(selectedCampaign.startDate).substring(
                            0,
                            10,
                          )
                        : "-"}{" "}
                      →{" "}
                      {selectedCampaign.endDate
                        ? formatDateTime(selectedCampaign.endDate).substring(
                            0,
                            10,
                          )
                        : "-"}
                    </Text>
                  </div>
                ) : (
                  <DatePicker.RangePicker
                    disabled={isEditMode}
                    style={{ width: "100%" }}
                    format="YYYY-MM-DD"
                    placeholder={["Start Date", "End Date"]}
                    disabledDate={(current) =>
                      current && current < dayjs().startOf("day")
                    }
                  />
                )}
              </Form.Item>
            </Col>

            <Col xs={24}>
              <Form.Item label="Description" name="description">
                <TextArea rows={2} placeholder="Optional short description" />
              </Form.Item>
            </Col>

            <Col xs={24} md={12}>
              <Form.Item
                label="Exclude Users With Previous Topup"
                name="isFirstTopupOnly"
                valuePropName="checked"
                initialValue={
                  selectedCampaign ? selectedCampaign.isFirstTopupOnly : false
                }
                tooltip="Users with prior topup history will be excluded from this campaign."
              >
                <Switch />
              </Form.Item>
            </Col>

            <Form.Item
              noStyle
              shouldUpdate={(prev, curr) => prev.category !== curr.category}
            >
              {({ getFieldValue }) => {
                const category = getFieldValue("category");

                if (category !== "userStatus") return null;

                return (
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="User Status"
                      name="status"
                      rules={[
                        {
                          required: true,
                          message: "Please select a user status",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select user status"
                        optionLabelProp="label"
                        disabled={isEditMode}
                      >
                        {status.map((item) => (
                          <Select.Option
                            key={item.key}
                            value={item.key}
                            label={item.name}
                          >
                            <div>
                              <div style={{ fontWeight: 500 }}>
                                <UserStatusTag statusKey={item.name}>
                                  {item.name}
                                </UserStatusTag>
                              </div>
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "#888",
                                }}
                              >
                                {item.title}
                              </div>
                            </div>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                );
              }}
            </Form.Item>
          </Row>
        </Card>

        {/* Multilingual Badge */}
        <Card
          size="small"
          title="Reward Badge Label (shown on dashboard)"
          bordered={false}
          style={{
            marginBottom: 20,
            background: "#fff",
            borderRadius: 12,
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} md={8}>
              <Form.Item label="English" name="badge_en">
                <TextArea rows={2} placeholder="e.g. FREE Credits" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Chinese" name="badge_zh">
                <TextArea rows={2} placeholder="e.g. 免费积分" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Malay" name="badge_ms">
                <TextArea rows={2} placeholder="e.g. Kredit Percuma" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* Bonus Table */}
        <Card
          size="small"
          title="Bonus per Package"
          bordered={false}
          style={{
            marginBottom: 20,
            background: "#fff",
            borderRadius: 12,
          }}
        >
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.category !== curr.category}
          >
            {selectedCampaign ? (
              renderEditTable(["campaignDetails"])
            ) : (
              <div>
                <div
                  style={{
                    marginBottom: 12,
                    padding: "10px 14px",
                    background: "#fafafa",
                    border: "1px solid #f0f0f0",
                    borderRadius: 8,
                  }}
                >
                  <Text type="secondary">
                    Set the bonus and optional limit per package.
                  </Text>
                </div>
                {renderCampaignTable(["campaignDetails"])}
              </div>
            )}
          </Form.Item>
        </Card>

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Space>
            <Button onClick={onCancel}>Reset</Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {isEditMode ? "Update Campaign" : "Create Campaign"}
            </Button>
          </Space>
        </div>
      </Form>
    </Card>
  );
};

export default BonusCampaignForm;
